<template>
    <v-container class="pt-5 pb-7">
        <v-layout row wrap class="pt-5" justify-content-center>
            <v-flex md6>
                <v-layout row>
                    <v-alert :value="unsavedChanges"
                             type="warning">
                        You've made changes to the course organization. Be sure to hit "save" before navigating away.
                    </v-alert>
                </v-layout>

                <v-layout row>
                    <v-menu offset-y>
                        <template v-slot:activator="{ on }">
                            <v-btn color="secondary" v-on="on">
                                New
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-tile @click="showNewDocumentModal = true">
                                <v-list-tile-title>New Document</v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile @click="showAddGroupingModal = true">
                                <v-list-tile-title>New Grouping</v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile @click="showAddLinkModal = true">
                                <v-list-tile-title>New Link</v-list-tile-title>
                            </v-list-tile>
                        </v-list>
                    </v-menu>


                    <v-btn color="secondary" @click="downloadEntireCourse">
                        Download All
                    </v-btn>
                    <v-btn color="secondary" @click="collapseAllFolders">
                        Collapse All
                    </v-btn>
                    <v-btn color="info" :loading="isSaving" @click="saveCategorizedDirectories">
                        Regenerate Index
                    </v-btn>
                    <v-spacer />
                    <v-btn color="success" :loading="isSaving" @click="saveCategorizedDirectories">
                        Save
                    </v-btn>
                </v-layout>

                <v-switch v-model="autoExpandOnDrag" label="Automatically Expand Directories While Categorizing" />

                <div class="pt-3 pl-2 ml-1">
                    <div v-if="initLoading" class="text-xs-center pt-5">
                        <v-progress-circular :size="70" :width="7" color="secondary" indeterminate />
                    </div>
                    <v-list v-else expand two-line class="categorized">
                        <draggable :list="categorizedDirectories" class="collapsed" :move="checkIfFile" :group="{ name: 'file' }" ghost-class="ghost-drop" @end="onEnd" @start="onStart">
                            <v-list-group v-for="item in categorizedDirectories" v-if="item.type === 'directory'" :key="item.id" prepend-icon="fas fa-folder" no-action :value="expandAll">
                                <template v-slot:activator>
                                    <v-list-tile>
                                        <v-list-tile-content>
                                            <v-list-tile-title>{{ item.name }}</v-list-tile-title>
                                            <v-list-tile-sub-title>
                                                <a href="javascript:;" @click.stop.prevent="editFile(item)">Edit Details</a>
                                                <v-tooltip bottom>
                                                    <template v-if="item.description" v-slot:activator="{ on }">
                                                        <v-icon class="ml-2" v-on="on">
                                                            fas fa-pen
                                                        </v-icon>
                                                    </template>
                                                    <span>Contains a Description</span>
                                                </v-tooltip>
                                                <v-tooltip bottom>
                                                    <template v-if="item.completionLength" v-slot:activator="{ on }">
                                                        <v-icon class="ml-1" v-on="on">
                                                            far fa-clock
                                                        </v-icon>
                                                    </template>
                                                    <span>Contains a Time Duration</span>
                                                </v-tooltip>
                                                <v-tooltip bottom>
                                                    <template v-if="item.childItems.length > 0" v-slot:activator="{ on }">
                                                        <div class="ml-1 d-inline-block" v-on="on">
                                                            {{ item.childItems.length }}
                                                        </div>
                                                    </template>
                                                    <span># of Items in Folder</span>
                                                </v-tooltip>
                                            </v-list-tile-sub-title>
                                        </v-list-tile-content>
                                    </v-list-tile>
                                </template>

                                <draggable v-if="item.childItems.length > 0" :list="item.childItems" class="collapsed" :group="{ name: 'file' }" ghost-class="ghost-drop" @end="onEnd" @start="onStart">
                                    <template v-for="childItem in item.childItems">
                                        <v-list-group v-if="childItem.type === 'directory'" :key="childItem.id" sub-group no-action :value="expandAll">
                                            <template v-slot:activator>
                                                <v-list-tile>
                                                    <v-list-tile-content>
                                                        <v-list-tile-title>{{ childItem.name }}</v-list-tile-title>
                                                        <v-list-tile-sub-title>
                                                            <a href="javascript:;" @click.stop.prevent="editFile(childItem)">Edit Details</a>
                                                            <v-tooltip bottom>
                                                                <template v-if="childItem.description" v-slot:activator="{ on }">
                                                                    <v-icon class="ml-2" v-on="on">
                                                                        fas fa-pen
                                                                    </v-icon>
                                                                </template>
                                                                <span>Contains a Description</span>
                                                            </v-tooltip>
                                                            <v-tooltip bottom>
                                                                <template v-if="childItem.completionLength" v-slot:activator="{ on }">
                                                                    <v-icon class="ml-1" v-on="on">
                                                                        far fa-clock
                                                                    </v-icon>
                                                                </template>
                                                                <span>Contains a Time Duration</span>
                                                            </v-tooltip>
                                                            <v-tooltip bottom>
                                                                <template v-if="childItem.childItems.length > 0" v-slot:activator="{ on }">
                                                                    <div class="ml-1 d-inline-block" v-on="on">
                                                                        {{ childItem.childItems.length }}
                                                                    </div>
                                                                </template>
                                                                <span># of Items in Folder</span>
                                                            </v-tooltip>
                                                        </v-list-tile-sub-title>
                                                    </v-list-tile-content>
                                                </v-list-tile>
                                            </template>
                                            <draggable v-if="childItem.childItems.length > 0" :list="childItem.childItems" class="collapsed" :group="{ name: 'file' }" ghost-class="ghost-drop" @end="onEnd" @start="onStart">
                                                <v-list-tile v-for="subchild in childItem.childItems" :key="subchild.id" class="file-list-tile" avatar>
                                                    <v-list-tile-avatar tile size="25">
                                                        <v-img :src="subchild.iconUrl" contain />
                                                    </v-list-tile-avatar>
                                                    <v-list-tile-content>
                                                        <v-list-tile-title>
                                                            <a v-if="subchild.type == 'link'" target="_blank" :href="'/resources/'+subchild.id">{{ subchild.name }}</a>
                                                            <a v-else target="_blank" :href="'/resources/'+subchild.id" :download="subchild.name">{{ subchild.name }}</a>
                                                        </v-list-tile-title>
                                                        <v-list-tile-sub-title>
                                                            <a href="javascript:;" @click.stop.prevent="editFile(subchild)">Edit Details</a>
                                                            <a v-if="subchild.editable" href="javascript:;" class="mb-0" @click="editDocument(subchild)">
                                                               | Edit Document
                                                            </a>
                                                            <v-tooltip bottom>
                                                                <template v-if="subchild.description" v-slot:activator="{ on }">
                                                                    <v-icon class="ml-2" v-on="on">
                                                                        fas fa-pen
                                                                    </v-icon>
                                                                </template>
                                                                <span>Contains a Description</span>
                                                            </v-tooltip>
                                                            <v-tooltip bottom>
                                                                <template v-if="subchild.completionLength" v-slot:activator="{ on }">
                                                                    <v-icon class="ml-1" v-on="on">
                                                                        far fa-clock
                                                                    </v-icon>
                                                                </template>
                                                                <span>Contains a Time Duration</span>
                                                            </v-tooltip>
                                                        </v-list-tile-sub-title>
                                                    </v-list-tile-content>
                                                    <v-list-tile-action>
                                                        <v-btn icon :loading="deleteFileId === subchild.id" @click="deleteFile(subchild.id, 'categorized')">
                                                            <v-icon color="grey lighten-1" small>
                                                                far fa-trash-alt
                                                            </v-icon>
                                                        </v-btn>
                                                    </v-list-tile-action>
                                                </v-list-tile>
                                            </draggable>
                                            <draggable v-if="showEmptyDropDivs && childItem.childItems.filter(item => item.type !== 'directory').length === 0" :list="childItem.childItems" class="drop-area" :group="{ name: 'file' }" ghost-class="ghost-drop" @end="onEnd" @start="onStart" />
                                        </v-list-group>

                                        <v-list-tile v-else :key="childItem.id" class="file-list-tile" avatar>
                                            <v-list-tile-avatar tile size="25">
                                                <v-img :src="childItem.iconUrl" contain />
                                            </v-list-tile-avatar>
                                            <v-list-tile-content>
                                                <v-list-tile-title>
                                                    <a v-if="childItem.type == 'link'" target="_blank" :href="'/resources/'+childItem.id">{{ childItem.name }}</a>
                                                    <a v-else target="_blank" :href="'/resources/'+childItem.id" :download="childItem.name">{{ childItem.name }}</a>
                                                </v-list-tile-title>
                                                <v-list-tile-sub-title>
                                                    <a href="javascript:;" @click.stop.prevent="editFile(childItem)">Edit Details</a>
                                                    <a v-if="childItem.editable" href="javascript:;" class="mb-0" @click="editDocument(childItem)">
                                                        | Edit Document
                                                    </a>
                                                    <v-tooltip bottom>
                                                        <template v-if="childItem.description" v-slot:activator="{ on }">
                                                            <v-icon class="ml-2" v-on="on">
                                                                fas fa-pen
                                                            </v-icon>
                                                        </template>
                                                        <span>Contains a Description</span>
                                                    </v-tooltip>
                                                    <v-tooltip bottom>
                                                        <template v-if="childItem.completionLength" v-slot:activator="{ on }">
                                                            <v-icon class="ml-1" v-on="on">
                                                                far fa-clock
                                                            </v-icon>
                                                        </template>
                                                        <span>Contains a Time Duration</span>
                                                    </v-tooltip>
                                                </v-list-tile-sub-title>
                                            </v-list-tile-content>
                                            <v-list-tile-action>
                                                <v-btn icon :loading="deleteFileId === childItem.id" @click="deleteFile(childItem.id, 'categorized')">
                                                    <v-icon color="grey lighten-1" small>
                                                        far fa-trash-alt
                                                    </v-icon>
                                                </v-btn>
                                            </v-list-tile-action>
                                        </v-list-tile>
                                    </template>
                                </draggable>
                                <draggable v-if="showEmptyDropDivs && item.childItems.filter(item => item.type !== 'directory').length === 0" :list="item.childItems" class="drop-area" :group="{ name: 'file' }" ghost-class="ghost-drop" @end="onEnd" @start="onStart" />
                            </v-list-group>
                            <v-list-tile v-else :key="item.id" class="file-list-tile" avatar>
                                <v-list-tile-avatar tile size="25">
                                    <v-img :src="item.iconUrl" contain />
                                </v-list-tile-avatar>
                                <v-list-tile-content>
                                    <v-list-tile-title>
                                        <a v-if="item.type == 'link'" target="_blank" :href="'/resources/'+item.id">{{ item.name }}</a>
                                        <a v-else target="_blank" :href="'/resources/'+item.id" :download="item.name">{{ item.name }}</a>
                                    </v-list-tile-title>
                                    <v-list-tile-sub-title>
                                        <a href="javascript:;" @click.stop.prevent="editFile(item)">Edit Details</a>
                                        <a v-if="item.editable" href="javascript:;" class="mb-0" @click="editDocument(item)">
                                           | Edit Document
                                        </a>
                                        <v-tooltip bottom>
                                            <template v-if="item.description" v-slot:activator="{ on }">
                                                <v-icon class="ml-2" v-on="on">
                                                    fas fa-pen
                                                </v-icon>
                                            </template>
                                            <span>Contains a Description</span>
                                        </v-tooltip>
                                        <v-tooltip bottom>
                                            <template v-if="item.completionLength" v-slot:activator="{ on }">
                                                <v-icon class="ml-1" v-on="on">
                                                    far fa-clock
                                                </v-icon>
                                            </template>
                                            <span>Contains a Time Duration</span>
                                        </v-tooltip>
                                    </v-list-tile-sub-title>
                                </v-list-tile-content>
                                <v-list-tile-action>
                                    <v-btn icon :loading="deleteFileId === item.id" @click="deleteFile(item.id, 'categorized')">
                                        <v-icon color="grey lighten-1" small>
                                            far fa-trash-alt
                                        </v-icon>
                                    </v-btn>
                                </v-list-tile-action>
                            </v-list-tile>
                        </draggable>
                    </v-list>
                </div>
            </v-flex>
            <v-flex md1 class="text-xs-center">
                <v-divider vertical class="ma-0" />
            </v-flex>
            <v-flex md4 sticky-container>
                <div v-sticky="shouldStick" sticky-side="top">
                    <p class="headline">
                        Uncategorized Files
                    </p>
                    <div v-if="initLoading" class="text-xs-center pt-5">
                        <v-progress-circular :size="70" :width="7" color="secondary" indeterminate />
                    </div>
                    <div v-else dense style="max-height: 750px;overflow-y: auto;">
                        <v-list>
                            <draggable id="UCList" tag="div" :list="uncategorizedFiles" :group="{ name: 'file' }" :sortable="false" :sort="false" ghost-class="ghost-drop" @end="onEnd" @start="onStart" @change="cleanseUncategorizedItem($event)">
                                <v-list-tile v-for="file in uncategorizedFiles" :key="file.id" avatar>
                                    <v-list-tile-avatar tile>
                                        <v-img :src="file.iconUrl" contain />
                                    </v-list-tile-avatar>

                                    <v-list-tile-content>
                                        <v-list-tile-title>
                                            <a v-if="file.type == 'link'" target="_blank" :href="'/resources/'+file.id">{{ file.name }}</a>
                                            <a v-else target="_blank" :href="'/resources/'+file.id" :download="file.name">{{ file.name }}</a>
                                        </v-list-tile-title>
                                    </v-list-tile-content>

                                    <v-list-tile-action>
                                        <v-btn icon ripple :loading="deleteFileId === file.id" @click="deleteFile(file.id, 'uncategorized')">
                                            <v-icon color="grey lighten-1" small>
                                                far fa-trash-alt
                                            </v-icon>
                                        </v-btn>
                                    </v-list-tile-action>
                                </v-list-tile>
                            </draggable>
                            <draggable v-if="showEmptyDropDivs && uncategorizedFiles.length === 0" id="UCDrop" :list="uncategorizedFiles" class="drop-area" :group="{ name: 'file' }" ghost-class="ghost-drop" @end="onEnd" @start="onStart" @change="cleanseUncategorizedItem($event)" />
                        </v-list>
                    </div>
                </div>
            </v-flex>
        </v-layout>
        <v-dialog v-model="showEditDocModal" max-width="450px" lazy persistent>
            <v-card class="text-xs-center py-5">
                <v-progress-circular :size="70" :width="7" color="secondary" indeterminate />
                <p class="mt-5">
                    Opening in a new window. Please wait...
                </p>
                <p>
                    Larger files and PowerPoint files will take longer to load.
                </p>
            </v-card>
        </v-dialog>
        <add-document-modal :dialog.sync="showNewDocumentModal" :course-id="courseId" :directories="categorizedDirectories" @getCategorized="getCategorized" @updateUnsavedState="updateUnsavedState" />
        <add-group-modal :dialog.sync="showAddGroupingModal" :course-id="courseId" :directories="categorizedDirectories" @getCategorized="getCategorized" @updateUnsavedState="updateUnsavedState" />
        <add-link-modal :dialog.sync="showAddLinkModal" :course-id="courseId" :directories="categorizedDirectories" @getCategorized="getCategorized" @updateUnsavedState="updateUnsavedState" />
        <edit-item-modal :dialog.sync="showEditItemModal" :course-id="courseId" :item="editingItemObject" :items.sync="categorizedDirectories" :uncategorized-items.sync="uncategorizedFiles" @updateUnsavedState="updateUnsavedState" />
    </v-container>
</template>
  <script>
  import axios from 'axios';
  import draggable from 'vuedraggable';
  import { mapActions } from 'vuex';
  import ManageCourseBase from './ManageCourseBase.vue';
  import AddGroupModal from './AddGroupModal.vue';
  import AddLinkModal from './AddLinkModal.vue';
  import EditItemModal from './EditItemModal.vue';
  import AddDocumentModal from './AddDocumentModal.vue';

  export default {
    components: {
      AddGroupModal,
      EditItemModal,
      draggable,
      AddLinkModal,
      AddDocumentModal
    },
    extends: ManageCourseBase,
    props: {
      selectedTab: {
        type: String,
        default: '',
      },
    },

    data: () => ({
      autoExpandOnDrag: false,
      uncategorizedSearch: '',
      unsavedChanges: false,
      shouldStick: false,
      showAddGroupingModal: false,
      showNewDocumentModal: false,
      showAddLinkModal: false,
      showEditItemModal: false,
      editingItemObject: null,
      uncategorizedFiles: [],
      categorizedFiles: [],
      categorizedDirectories: [],
      showEmptyDropDivs: false,
      deleteFileId: null,
      expandAll: false,
      isSaving: false,
        initLoading: true,
        showEditDocModal: false
    }),

    computed: {
      filteredUncategorizedFiles() {
        return this.uncategorizedFiles.filter(option => option.name.toLowerCase().indexOf(this.uncategorizedSearch.toLowerCase()) >= 0);
      },
    },

    watch: {
      selectedTab(newVal) {
        if (newVal === 'Organization') {
          this.init();
          this.shouldStick = true;
        } else {
          this.shouldStick = true;
        }
      },

      //unsavedChanges(newVal) {
      //  if (newVal) {
      //    setTimeout(() => {
      //      this.saveCategorizedDirectories();
      //    }, 10 * 1000);
      //  }
      //},
    },

    mounted() {
      this.initLoading = true;
      this.init().then(() => {
        this.initLoading = false;
      });
      window.addEventListener('beforeunload', event => this.detectBrowserLeave(event));
    },

    methods: {
      ...mapActions({
        setToast: 'toasts/addToGlobalToastQueue',
      }),

      detectBrowserLeave(event) {
        if (this.unsavedChanges) {
          event.returnValue = 'You have unsaved changes. Are you you want to leave?';
        }
      },

      async init() {
        await this.getUncategorizedFiles();
        await this.getCategorized();
      },
      editFile(item) {
        this.editingItemObject = item;
        this.showEditItemModal = true;
      },

      async getUncategorizedFiles() {
        const { data } = await axios.get(`/api/courses/${this.courseId}/items/uncategorized`);
        this.uncategorizedFiles = data;
      },

      async getCategorized() {
        const { data } = await axios.get(`/api/courses/${this.courseId}/items/categorized`);
        this.categorizedDirectories = data;
      },

        async editDocument(selectedFile) {
            try {
                this.showEditDocModal = true;
                const { data } = await axios.get(`/api/courses/${this.courseId}/items/${selectedFile.id}/zoho-edit`);
                window.open(data);
            } catch (error) {
                this.setToast({
                    color: 'error',
                    message: error.response ? error.response.data : 'This file is not an editable file.',
                    multiline: false,
                });
            }
            this.showEditDocModal = false;
        },
      async deleteFile(itemId, type = 'uncategorized') {
        await this.saveCategorizedDirectories(false);
        this.deleteFileId = itemId;
        await axios.delete(`/api/courses/${this.courseId}/items/${itemId}`);
        if (type === 'uncategorized') {
          this.getUncategorizedFiles();
        } else {
          this.getCategorized();
        }
        this.deleteFileId = null;
      },

      async downloadEntireCourse() {
        const { data } = await axios.get(`/api/courses/${this.courseId}/`);
        if (data.fullDownloadUrl) {
          const { data } = await axios.get(`/api/courses/${this.courseId}/download`);
          window.location = data;
        } else {
          alert('Course is still being indexed, please wait a few minutes and try again');
        }
      },

      async saveCategorizedDirectories(showToast = true) {
        this.isSaving = true;
        this.categorizedDirectories.forEach((item, parentIndex) => {
          item.categorized = true;
          item.parentItemId = null;
          item.sortIndex = parentIndex;
          if (item.childItems) {
            item.childItems.forEach((child, childIndex) => {
              child.categorized = true;
              child.parentItemId = item.id;
              child.sortIndex = childIndex;
              if (child.childItems) {
                child.childItems.forEach((subChild, subChildIndex) => {
                  subChild.categorized = true;
                  subChild.parentItemId = child.id;
                  subChild.sortIndex = subChildIndex;
                });
              }
            });
          }
        });
        try {
          await axios.put(`/api/courses/${this.courseId}/items`, this.categorizedDirectories);
          if (showToast) {
            this.setToast({
              color: 'success',
              message: 'Files have been saved!',
              multiline: false,
            });
          }
          this.updateUnsavedState(false);
          this.isSaving = false;
        } catch (error) {
          this.setToast({
            color: 'error',
            message: error.response.data[0],
            multiline: true,
          });
          this.isSaving = false;
        }
      },

      updateUnsavedState(val) {
        this.unsavedChanges = val;
      },

      async cleanseUncategorizedItem(event) {
        if (Object.keys(event)[0] !== 'added') return;
        const item = event.added.element;
        item.categorized = false;
        item.parentItemId = null;
        item.childItems = [];
        item.sortIndex = 0;
        await axios.patch(`/api/courses/${this.courseId}/items/${item.id}`, item);
        this.saveCategorizedDirectories();
      },

      checkIfFile(event) {
        if (event.to.id === 'UCList' || event.to.id === 'UCDrop') {
          if (event.draggedContext.element.type !== 'file') {
            return false;
          }
        }

        return true;
      },

      onStart() {
        this.showEmptyDropDivs = true;
        this.expandAll = this.autoExpandOnDrag;
      },

      onEnd() {
        this.showEmptyDropDivs = false;
        this.unsavedChanges = true;
      },

      collapseAllFolders() {
        this.expandAll = true;
        this.$nextTick(() => {
          this.expandAll = false;
        });
      },
    },
  };
  </script>

<style scoped>
  .drop-area {
    width: 100%;
    color: black;
    font-size: 15px;
    font-weight: bold;
    line-height: 1.3em;
    border: 2px dashed #d2202f;
    border-radius: 10px;
    font-weight: normal;

    padding: 0 16px;
    min-height: 70px;
  }

  .ghost-drop {
    opacity: 0.5;
    border: 2px dashed black;
    padding: 10px;
  }
  /deep/ .v-input--selection-controls.v-input .v-label {
    margin-bottom: 0;
  }
</style>

<style>
  .categorized .v-list__group--active {
    border: 1px solid rgba(0, 0, 0, 0.664);
  }
</style>